import { useMemo, useRef, useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import './tab-container.styles.scss';

/**
 * Tab configuration object
 * @typedef {Object} TabConfig
 * @property {string} label - Display label for the tab
 * @property {string} key - Unique key identifier for the tab
 * @property {React.ComponentType} component - Component to render when tab is active
 * @property {string} pathname - Pathname to use for navigation when this tab is active
 */

/**
 * TabContainer Component
 * A reusable tab container with keyboard navigation and focus management
 * 
 * @param {Object} props
 * @param {TabConfig[]} props.tabs - Array of tab configurations (each tab should have pathname)
 * @param {string} props.defaultTab - Default tab key to show if no tab state in location
 * @param {Function} props.onTabChange - Optional callback when tab changes (receives tabKey)
 * @param {string} props.search - Search query string for navigation (optional)
 * @param {string} props.containerClassName - Additional CSS class for container
 * @param {string} props.tabsContainerClassName - Additional CSS class for tabs container
 * @param {string} props.tabContentClassName - Additional CSS class for tab content
 * @param {string} props.tabLinkClassName - Additional CSS class for tab links
 * @param {Object} props.tabContentProps - Props to pass to the active tab component
 */
const TabContainer = ({
  tabs = [],
  defaultTab,
  onTabChange,
  search,
  containerClassName = '',
  tabsContainerClassName = '',
  tabContentClassName = '',
  tabLinkClassName = '',
  tabContentProps = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tab: tabState } = location.state || {};
  const tabRefs = useRef([]);

  const activeTab = useMemo(() => {
    if (tabState) {
      return tabs.find((tab) => tab.key === tabState) || tabs[0];
    }
    if (defaultTab) {
      return tabs.find((tab) => tab.key === defaultTab) || tabs[0];
    }
    return tabs[0];
  }, [tabState, defaultTab, tabs]);

  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, tabs.length);
  }, [tabs.length]);

  const handleTabChange = (key) => {
    const selectedTab = tabs.find((tab) => tab.key === key);
    const targetPathname = selectedTab?.pathname || location.pathname;
    const targetSearch = search !== undefined ? search : location.search;

    navigate(`${targetPathname}${targetSearch}`, {
      state: {
        tab: key
      }
    });

    if (onTabChange) {
      onTabChange(key);
    }
  };

  const handleKeyDown = (e, currentIndex) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % tabs.length;
      handleTabChange(tabs[nextIndex].key);
      setTimeout(() => {
        const nextTab = tabRefs.current[nextIndex];
        if (nextTab) {
          nextTab.focus();
        }
      }, 0);
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      handleTabChange(tabs[prevIndex].key);
      setTimeout(() => {
        const prevTab = tabRefs.current[prevIndex];
        if (prevTab) {
          prevTab.focus();
        }
      }, 0);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % tabs.length;
      handleTabChange(tabs[nextIndex].key);
      setTimeout(() => {
        const nextTab = tabRefs.current[nextIndex];
        if (nextTab) {
          nextTab.focus();
        }
      }, 0);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      handleTabChange(tabs[prevIndex].key);
      setTimeout(() => {
        const prevTab = tabRefs.current[prevIndex];
        if (prevTab) {
          prevTab.focus();
        }
      }, 0);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTabChange(tabs[currentIndex].key);
    }
  };

  if (!activeTab) {
    return null;
  }

  const ActiveComponent = activeTab.component;

  return (
    <div className={`tab-container ${containerClassName}`}>
      <div className={`tabs-container hide-scrollbar overflow-hidden ${tabsContainerClassName}`}>
        <Nav variant="tabs" className="fullwidth" role="tablist">
          {tabs.map((tab, index) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                active={activeTab.key === tab.key}
                onClick={() => handleTabChange(tab.key)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                className={`f-14 ${tabLinkClassName}`}
                role="tab"
                tabIndex={0}
                aria-selected={activeTab.key === tab.key}
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </div>
      <div className={`tab-content ${tabContentClassName}`} role="tabpanel">
        <ActiveComponent {...tabContentProps} />
      </div>
    </div>
  );
};

export default TabContainer;

